import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import apiClient from "../../apiClient/apiClient";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_SESSION_DATA, FETCH_TIMELINE_VIOLATIONS } from "../../apiClient/apiEndpoints";
import { NextFrame, NextIcon, NoStatsIcon, PrevFrame, PrevIcon, ZoomInIcon, ZoomOutIcon } from "../../assets/Icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import CustomCalender from "./CustomCalender";
import { savePersonList } from "../../store/slice/violationSlice";

const generateTime = (time, zoom) => {
  let startTime = null;
  let endTime = null;
  if (time) {
    const date = moment(time);
    if (!zoom) {
      if (date.format("HH:mm") === "23:59") {
        startTime = "23:00";
        endTime = "00:00";
      } else if (date.format("mm") !== "00") {
        startTime = date.clone().startOf("hour").format("HH:mm");
        endTime = date.format("HH:mm");
      } else {
        startTime = date.clone().subtract(1, "hours").format("HH:mm");
        endTime = date.format("HH:mm");
      }
    } else {
      startTime = date.clone().subtract(10, "minutes").format("HH:mm");
      endTime = date.format("HH:mm");
    }
  }
  return { startTime, endTime };
};

const MonitoringDash = () => {
  const params = useParams();
  const configData = useSelector((state) => state?.config);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const dateParams = query.get("date");
  let urlDate = "";
  let urlTime = "";
  if (dateParams) {
    urlDate = new Date(dateParams);
    urlDate.setHours(23, 59, 59, 999);
    urlTime = moment(new Date(dateParams)).toLocaleString();
  }
  const API_URL = configData?.config?.API_URL;
  const [activeSpan, setActiveSpan] = useState(urlTime || "");
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [selectedDate, setSelectedDate] = useState(urlDate ? urlDate : null);
  const [activeSpanRecord, setActiveSpanRecord] = useState("");
  const [violationCount, setViolationCount] = useState([]);
  const [zoom, setZoom] = useState(false);
  const presentTime = moment();
  const { startTime, endTime } = useMemo(() => generateTime(activeSpan, zoom), [activeSpan, zoom]);
  const activePrevButton = useMemo(() => timeline[0]?.time !== activeSpan, [activeSpan, timeline.length]);
  const activeNextButton = useMemo(() => {
    return (
      timeline[timeline?.length - 1]?.time !== activeSpan &&
      timeline[timeline.findIndex((item) => item.time === activeSpan) + 1]?.isActive
    );
  }, [activeSpan, timeline.length]);

  const handleToggleClass = (time) => {
    setActiveSpan(time);
  };

  const zoomIn = () => {
    setZoom(true);
    setActiveSpanRecord(activeSpan);
  };

  const zoomOut = () => {
    setZoom(false);
    setActiveSpan(activeSpanRecord);
  };

  const fetchViolationsInTime = async () => {
    const queryParams = {
      filter: timeline?.map((item) => item.time),
    };
    const res = await apiClient.get(`${API_URL}${FETCH_TIMELINE_VIOLATIONS}/${params.instanceId}`, {
      params: queryParams,
    });
    setViolationCount(res);
  };

  const fetchSessionData = async () => {
    setLoading(true);
    let queryParams = {};
    if (activeSpan) {
      queryParams.start = moment(activeSpan).toISOString();
    }
    if (zoom) {
      queryParams.timeDuration = 10;
      queryParams.timeUnit = "minutes";
    }
    const res = await apiClient.get(`${API_URL}${FETCH_SESSION_DATA}/${params.instanceId}`, { params: queryParams });
    setSessionData(res);
    const personViolationList = [];
    res.sessions?.forEach((item) => {
      personViolationList.push(...item.children);
    });
    setLoading(false);
    dispatch(savePersonList(personViolationList));
  };

  const onNextClick = () => {
    const idx = timeline.findIndex((item) => item.time === activeSpan);
    setActiveSpan(timeline[idx + 1].time);
  };

  const onPrevClick = () => {
    const idx = timeline.findIndex((item) => item.time === activeSpan);
    setActiveSpan(timeline[idx - 1].time);
  };

  const generateTimeline = () => {
    const timeline = [];
    const now = selectedDate ? moment(selectedDate) : presentTime.endOf("day");
    const dayStartTime = now.clone().startOf("day");
    while (now > dayStartTime) {
      dayStartTime.add(1, "hours");
      if (dayStartTime >= now) {
        const currentTime = new Date(presentTime.toLocaleString());
        currentTime.setHours(new Date(presentTime.toLocaleString()).getHours() + 1, 0, 0, 0);
        const isActive = new Date(dayStartTime.toLocaleString()).getTime() <= currentTime.getTime();
        timeline.push({ time: now.toLocaleString(), isActive });
      } else {
        const currentTime = new Date(presentTime.toLocaleString());
        currentTime.setHours(new Date(presentTime.toLocaleString()).getHours() + 1, 0, 0, 0);
        const isActive = new Date(dayStartTime.toLocaleString()).getTime() <= currentTime.getTime();
        timeline.push({ time: dayStartTime.toLocaleString(), isActive });
      }
    }
    setTimeline(timeline);
    return timeline;
  };

  const generateTenMinutesTimeline = () => {
    const timeline = [];
    const now = moment(activeSpan);
    const hourStartTime = now.clone().subtract(1, "hours").startOf("hour");
    while (now > hourStartTime) {
      hourStartTime.add(10, "minutes");
      if (hourStartTime >= now) {
        const currentTime = new Date(presentTime.toLocaleString());
        currentTime.setMinutes(new Date(presentTime.toLocaleString()).getMinutes() + 10, 0, 0);
        const isActive = new Date(hourStartTime.toLocaleString()).getTime() <= currentTime.getTime();
        timeline.push({ time: now.toLocaleString(), isActive });
      } else {
        const currentTime = new Date(presentTime.toLocaleString());
        currentTime.setMinutes(new Date(presentTime.toLocaleString()).getMinutes() + 10, 0, 0);
        const isActive = new Date(hourStartTime.toLocaleString()).getTime() <= currentTime.getTime();
        timeline.push({ time: hourStartTime.toLocaleString(), isActive });
      }
    }
    setTimeline(timeline);
    setActiveSpan(timeline[0].time);
  };

  const navigateToPerPersonPage = () => {
    const model = query.get("model");
    const area = query.get("area");
    navigate(
      `/monitoring/detail/${params.instanceId}?start=${moment(
        activeSpan
      )?.toISOString()}&model=${model}&area=${area}&zoom=${zoom}`
    );
  };

  const initializeTimeLine = () => {
    const time = generateTimeline();
    initializeActiveSpan(time);
  };

  const initializeActiveSpan = (timeArray) => {
    const cloneDate = new Date(selectedDate);
    if (moment(cloneDate).format("DD-MM-YYYY") !== presentTime.format("DD-MM-YYYY")) {
      const prevTimeSpan = new Date(activeSpan);
      prevTimeSpan.setFullYear(cloneDate.getFullYear(), cloneDate.getMonth(), cloneDate.getDate());
      if (
        !timeArray?.find((item) => item?.time === activeSpan) &&
        moment(activeSpan).format("DD-MM-YYYY") === moment(timeArray[0].time).format("DD-MM-YYYY")
      ) {
        const startHour = moment(activeSpan).startOf("hour").toLocaleString();
        const index = timeArray?.findIndex((item) => item?.time === startHour);
        setActiveSpan(timeArray[index + 1]?.time);
      } else {
        setActiveSpan(moment(prevTimeSpan).toLocaleString());
      }
    } else {
      const clone = presentTime.clone();
      setActiveSpan(clone.add(1, "hours").startOf("hour").toLocaleString());
    }
  };

  const handleDateSelection = (value) => {
    setZoom(false);
    setSelectedDate(value);
  };

  useEffect(() => {
    if (!urlDate || !urlTime) {
      generateTimeline();
      setSelectedDate(new Date(presentTime.toLocaleString()));
    }
  }, []);

  useEffect(() => {
    if (activeSpan) fetchSessionData();
  }, [activeSpan]);

  useEffect(() => {
    if (timeline.length) fetchViolationsInTime();
  }, [timeline]);

  useEffect(() => {
    initializeTimeLine();
  }, [selectedDate]);

  useEffect(() => {
    if (zoom) {
      generateTenMinutesTimeline();
    } else {
      generateTimeline();
    }
  }, [zoom]);

  return (
    <div className="container-fluid admindashboard p-4 pt-2 bg-grey-main">
      <span className="backbtn" onClick={() => navigate("/monitoring")}>
        {" "}
        <PrevIcon /> Back
      </span>
      <div className="monitor-dash">
        <div className="view-nd-detail">
          <div className="view-box">
            {!loading ? (
              sessionData?.sessions?.length > 0 && <img src={sessionData?.sessions?.[0]?.data?.images[0]} />
            ) : (
              <div className="snap-gallery">
                <div className="snap-gallery-load">
                  <svg viewBox="25 25 50 50">
                    <circle r="20" cy="50" cx="50"></circle>
                  </svg>
                </div>
              </div>
            )}

            {!loading && sessionData?.sessions?.length < 1 && (
              <div className="nothing-found">
                <NoStatsIcon />
                <p>No Data Recorded!</p>
              </div>
            )}
          </div>

          <div className="details-here">
            <CustomCalender selectedDate={selectedDate} setSelectedDate={handleDateSelection} />
            <div className="detail-item person">
              <h6>Person Detected</h6>
              <div className="detail-list">
                <p>
                  Person with Violation <span>{sessionData?.personWithViolation}</span>
                </p>
                <p>
                  Person without Violation <span>{sessionData?.personWithNonviolation}</span>
                </p>
                <p>
                  Total Person<span> {sessionData?.totalPerson} </span>
                </p>
              </div>
            </div>
            <div className="detail-item">
              <h6>PPE Violation</h6>
              <div className="detail-list">
                <p>
                  Helmet Violation <span>{sessionData?.detection?.violation.helmet}</span>
                </p>
                <p>
                  Gloves Violation <span>{sessionData?.detection?.violation.gloves}</span>
                </p>
                <p>
                  Boots Violation<span>{sessionData?.detection?.violation.boots}</span>
                </p>
                <p>
                  Safety Vest Violation<span>{sessionData?.detection?.violation.vest}</span>
                </p>
                <p>
                  Glasses Violation<span>{sessionData?.detection?.violation.glasses}</span>
                </p>
                <p>
                  Uniform Violation<span>{sessionData?.detection?.violation.uniform}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="timeline-box">
          <div className="timeline-function">
            <div className="d-flex align-items-center justify-content-start">
              <div className="next-prev">
                <Tooltip title="Previous Time frame">
                  <span
                    onClick={activePrevButton ? onPrevClick : () => {}}
                    className={!activePrevButton ? "disabled" : ""}
                  >
                    <NextFrame />
                  </span>
                </Tooltip>
                <Tooltip title="Next Time frame">
                  <span
                    onClick={activeNextButton ? onNextClick : () => {}}
                    className={!activeNextButton ? "disabled" : ""}
                  >
                    <PrevFrame />
                  </span>
                </Tooltip>
              </div>
              <p className="timeline-info ps-3">
                Showing Results for{" "}
                <span>
                  {" "}
                  {moment(activeSpan).format("DD-MMM-YYYY")} ({startTime} - {endTime}){" "}
                </span>
              </p>
            </div>

            <div className="d-flex gap-3 align-item-center justify-content-end">
              <div className="zoom-btns">
                <span className={zoom ? "active" : ""} onClick={!zoom ? zoomIn : () => {}}>
                  <ZoomInIcon />
                </span>
                <span className={!zoom ? "active" : ""} onClick={zoom ? zoomOut : () => {}}>
                  <ZoomOutIcon />
                </span>
              </div>
              <span className="backbtn view-more " onClick={navigateToPerPersonPage}>
                {" "}
                Detailed View <NextIcon />{" "}
              </span>
            </div>
          </div>
          <div className="timerange-box">
            <div className="time-stamps">
              {timeline.map((item) => (
                <div className="time-stamps-item">
                  <span>
                    {moment(item.time).format("HH:mm") == "23:59" ? "00:00" : moment(item.time).format("HH:mm")}
                  </span>
                </div>
              ))}
            </div>
            <div className="range-div">
              <div className="intervals">
                {timeline.map((time, index) => {
                  const startTime = index === 0 ? "00:00" : moment(timeline[index - 1].time).format("HH:mm");
                  const endTime =
                    moment(timeline[index].time).format("HH:mm") == "23:59"
                      ? "00:00"
                      : moment(timeline[index].time).format("HH:mm");
                  return (
                    <Tooltip title={`${startTime} - ${endTime}`}>
                      <span
                        key={index}
                        className={
                          time.time === activeSpan
                            ? // with-violation class condition for violation.
                              `time-interval selected-one ${
                                time.isActive &&
                                violationCount?.[
                                  violationCount?.findIndex((violation) => violation?.label === time?.time)
                                ]?.violations
                                  ? "with-violation"
                                  : ""
                              }`
                            : `time-interval ${
                                time.isActive
                                  ? violationCount?.[
                                      violationCount?.findIndex((violation) => violation?.label === time?.time)
                                    ]?.violations
                                    ? "with-violation"
                                    : ""
                                  : "no-time"
                              }`
                        }
                        onClick={time.isActive ? () => handleToggleClass(time.time) : () => {}}
                      >
                        {""}
                        <span className="left-bar">{""}</span>
                        <span className="right-bar">{""}</span>
                      </span>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="range-info">
            <div className="range-item">
              <span>{""}</span>
              <p>Selected Time</p>
            </div>
            <div className="range-item">
              <span>{""}</span>
              <p>Available Time</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitoringDash;
